import api from './api';

export interface AuditLog {
  id: string;
  userId: string;
  userEmail: string;
  tenantId: string;
  action: string;
  resource?: string;
  resourceId?: string;
  description: string;
  metadata?: Record<string, any>;
  ipAddress?: string;
  userAgent?: string;
  timestamp: string;
}

export interface AuditLogPage {
  content: AuditLog[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

class AuditService {
  private baseUrl = '/student/audit';

  /**
   * Get recent activities for student dashboard
   */
  async getRecentActivities(limit: number = 10): Promise<AuditLog[]> {
    try {
      const response = await api.get(`${this.baseUrl}/recent`, {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch recent activities:', error);
      return [];
    }
  }

  /**
   * Get user activities with pagination
   */
  async getUserActivities(page: number = 0, size: number = 20): Promise<AuditLogPage> {
    try {
      const response = await api.get(`${this.baseUrl}/activities`, {
        params: { page, size }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user activities:', error);
      return {
        content: [],
        totalElements: 0,
        totalPages: 0,
        size: 0,
        number: 0
      };
    }
  }

  /**
   * Get login history
   */
  async getLoginHistory(limit: number = 10): Promise<AuditLog[]> {
    try {
      const response = await api.get(`${this.baseUrl}/login-history`, {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch login history:', error);
      return [];
    }
  }

  /**
   * Get activities by action type
   */
  async getActivitiesByAction(action: string, page: number = 0, size: number = 20): Promise<AuditLogPage> {
    try {
      const response = await api.get(`${this.baseUrl}/by-action`, {
        params: { action, page, size }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch activities by action:', error);
      return {
        content: [],
        totalElements: 0,
        totalPages: 0,
        size: 0,
        number: 0
      };
    }
  }

  /**
   * Get activities in date range
   */
  async getActivitiesInDateRange(
    startDate: string,
    endDate: string,
    page: number = 0,
    size: number = 20
  ): Promise<AuditLogPage> {
    try {
      const response = await api.get(`${this.baseUrl}/date-range`, {
        params: { startDate, endDate, page, size }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch activities in date range:', error);
      return {
        content: [],
        totalElements: 0,
        totalPages: 0,
        size: 0,
        number: 0
      };
    }
  }

  /**
   * Format action for display
   */
  formatAction(action: string): string {
    const actionMap: Record<string, string> = {
      'LOGIN': 'Logged in',
      'LOGOUT': 'Logged out',
      'TEST_START': 'Started test',
      'TEST_SUBMIT': 'Submitted test',
      'TEST_RESUME': 'Resumed test',
      'ANSWER_SAVE': 'Saved answer',
      'ANSWER_CLEAR': 'Cleared answer',
      'QUESTION_NAVIGATE': 'Navigated to question',
      'PROFILE_VIEW': 'Viewed profile',
      'RESULTS_VIEW': 'Viewed results',
      'PASSWORD_CHANGE': 'Changed password',
      'SETTINGS_UPDATE': 'Updated settings'
    };

    return actionMap[action] || action;
  }

  /**
   * Get action icon
   */
  getActionIcon(action: string): string {
    const iconMap: Record<string, string> = {
      'LOGIN': '🔐',
      'LOGOUT': '🚪',
      'TEST_START': '▶️',
      'TEST_SUBMIT': '✅',
      'TEST_RESUME': '⏯️',
      'ANSWER_SAVE': '💾',
      'ANSWER_CLEAR': '🗑️',
      'QUESTION_NAVIGATE': '🧭',
      'PROFILE_VIEW': '👤',
      'RESULTS_VIEW': '📊',
      'PASSWORD_CHANGE': '🔑',
      'SETTINGS_UPDATE': '⚙️'
    };

    return iconMap[action] || '📝';
  }

  /**
   * Log test analytics data
   */
  async logTestAnalytics(testId: string, analyticsData: {
    questionTimeSpent?: Record<string, number>;
    totalTimeSpent?: number;
    questionsAnswered?: number;
    questionsMarked?: number;
  }): Promise<void> {
    try {
      await api.post(`${this.baseUrl}/test-analytics`, {
        testId,
        ...analyticsData,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to log test analytics:', error);
      // Don't throw error to avoid disrupting test flow
    }
  }

  /**
   * Format timestamp for display
   */
  formatTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  }
}

export const auditService = new AuditService();
export default auditService;
